import { Alert, Stack, Typography } from '@mui/material'
import { VesselVisitBerthAndTimingFormStore } from '@planning/pages/VesselVisit/Stores/VesselVisitBerthAndTimingFormStore'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { Dayjs } from 'dayjs'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { VesselVisitViewAttribute } from '../../molecules/VesselVisitViewAttribute'

type VesselVisitBerthAndTimingViewModeProps = {
  store: VesselVisitBerthAndTimingFormStore
}

export const VesselVisitBerthAndTimingViewMode: FC<VesselVisitBerthAndTimingViewModeProps> =
  observer(({ store }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const {
      etaDate,
      etaTime,
      etbDate,
      etbTime,
      etdDate,
      etdTime,
      draftArrival,
      draftDeparture,
      dockingSide,
      pier,
      qmmFrom,
      qmmTo,
      visitType,
      length,
    } = store

    const hasValue = (...values: Array<string | number | null | undefined | Dayjs>) =>
      values.some(val => {
        if (val === null || val === undefined || val === '') {
          return false
        }

        if (typeof val === 'number') {
          return val > 0
        }

        if (typeof val === 'string') {
          return val.trim().length > 0
        }

        return true
      })

    const formatDateTime = (date: Dayjs | null, time: Dayjs | null) => {
      if (!date) return undefined
      const dateStr = date.format('DD/MM/YYYY')
      const timeStr = time ? time.format('HH:mm') : '--:--'
      return `${dateStr}, ${timeStr}`
    }

    const hasAnyData = hasValue(
      draftArrival.value,
      draftDeparture.value,
      pier.value?.name,
      qmmFrom.value,
      qmmTo.value,
      etaDate.value,
      etaTime.value,
      etbDate.value,
      etbTime.value,
      etdDate.value,
      etdTime.value,
      length.value,
    )

    if (!hasAnyData) {
      return (
        <Typography variant='body2' color={theme.palette.text.secondary}>
          {t('noInformationYet', 'No information yet')}
        </Typography>
      )
    }

    const { berths, conflictingBerthDraft } = store

    return (
      <Stack direction='column'>
        {!!conflictingBerthDraft && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            <Typography variant='body2' color='#7A4F01'>
              {t(
                'draftWarning',
                `Arrival/departure drafts (${draftArrival.value}m/${draftDeparture.value}m) are deeper than the berth draft (${conflictingBerthDraft}m).`,
              )}
            </Typography>
          </Alert>
        )}
        {hasValue(draftArrival.value, draftDeparture.value) && (
          <VesselVisitViewAttribute
            label={t('arrivalDepartureDrafts', 'Arrival/departure drafts')}
            value={`${draftArrival.value || '-'}m/${draftDeparture.value || '-'}m`}
          />
        )}

        {hasValue(dockingSide.value) && (
          <VesselVisitViewAttribute
            label={t('dockingSide', 'Docking side')}
            value={dockingSide.value ?? ''}
          />
        )}

        {hasValue(qmmFrom.value, qmmTo.value, pier.value?.name) && (
          <VesselVisitViewAttribute
            label={t('qmm', 'QMM')}
            value={`${qmmFrom.value || '-'} to ${qmmTo.value || '-'}m${
              berths.value.length > 0 ? ` (${berths.value.map(b => b.name).join(', ')})` : ''
            }`}
          />
        )}

        {hasValue(etaDate.value, etaTime.value) && (
          <VesselVisitViewAttribute
            label={t('eta', 'ETA')}
            value={formatDateTime(etaDate.value, etaTime.value) ?? ''}
          />
        )}

        {hasValue(etbDate.value, etbTime.value) && (
          <VesselVisitViewAttribute
            label={t('etb', 'ETB')}
            value={formatDateTime(etbDate.value, etbTime.value) ?? ''}
          />
        )}

        {hasValue(etdDate.value, etdTime.value) && (
          <VesselVisitViewAttribute
            label={t('etd', 'ETD')}
            value={formatDateTime(etdDate.value, etdTime.value) ?? ''}
          />
        )}

        {hasValue(visitType.value) && (
          <VesselVisitViewAttribute
            label={t('visitRecurrence', 'Visit recurrence')}
            value={visitType.value ?? ''}
          />
        )}

        {hasValue(length.value) && (
          <VesselVisitViewAttribute
            label={t('vesselLength', 'Vessel length')}
            value={`${length.value}m`}
          />
        )}
      </Stack>
    )
  })
